<template>
	<DefaultLayout :channelCode="shortID" :scroll-y="false">
		<template #nav-right>
			<div class="nav-right-container">
				<!--   ViewID   -->
				<div v-if="isDevMode" class="nav-right-button">
					<span class="meet-title-text">
						{{ `Viewer ID:${viewerID}` }}
					</span>
				</div>
				<!--匯出按鈕，只有 dev 模式-->
				<div v-if="isDevMode" id="export-button" class="nav-right-button" @click.stop="handleExportBtnOnClick">
					<span class="meet-title-text">{{ t('Download') }}</span>
				</div>
				<!--分享鏈結-->
				<NavRightButton icon="share" :label="t('Share')" @click="handleShareButtonOnClick" />
				<!--翻譯語言-->
				<NavRightButton id="translation-language-button" icon="globe" :label="t('LanguageSettings')"
								:open="showLangOverlay" @click.stop="handleTranslateToBtnOnClick" />
				<!--直播標籤-->
				<LiveLabel :is-live="isLive" />
			</div>
		</template>
		<template #content>
			<div class="tutorial-shell" :class="{ 'has-source': showOriginText }">
				<div class="pane-stack"
					 :style="{ '--transcript-font-size': `${fontSize}px`, '--paragraph-gap': `${paragraphGap}px` }">
					<!--TTS 視窗-->
					<TranscriptPane v-if="showTtsPane" pane="tts" :items="ttsPaneState.items"
									:has-more="ttsPaneState.hasMore" :show-head="showPaneHead"
									:show-speakers="showSpeakers" :translating="false" :lang="targetLang"
									:active-index="currentVoiceIndex" :reveal-clock="ttsRevealClock"
									@select="seekVoiceTo">
						<template #actions>
							<!--Go to Latest：落後才能按，追上之後 disabled 而不是隱藏，標題列右側才不會跳動-->
							<button class="pane-catch-up" type="button" :disabled="!isTtsBehind"
									@click="handleCatchUpOnClick">
								<span>{{ t('Pane.CatchUp') }}</span>
							</button>
							<!--語速膠囊-->
							<button id="speech-speed-button" class="pane-pill" :class="{ 'is-open': showSpeechSpeedOverlay }"
									type="button" :title="t('Tips.Icon.SpeechRate')" @click.stop="handleSpeechSpeedIconOnClick">
								<span>{{ speechSpeed }}x</span>
							</button>
						</template>
					</TranscriptPane>
					<div class="pane-row">
						<!--原文視窗-->
						<TranscriptPane v-if="showOriginText" pane="source" :items="sourcePaneState.items"
										:has-more="sourcePaneState.hasMore" :show-head="showPaneHead"
										:show-speakers="showSpeakers" :translating="isLive && isTranslating" />
						<!--譯文視窗-->
						<TranscriptPane ref="translationPane" pane="translation" :items="translationPaneState.items"
										:has-more="translationPaneState.hasMore" :show-head="showPaneHead"
										:show-speakers="showSpeakers" :translating="isLive && isTranslating"
										:lang="targetLang" />
					</div>
				</div>
				<!--控制列-->
				<ControlBar :show-tts="showTts" :tts-available="ttsAvailable" :show-origin="showOriginText"
							:spacing-open="showSpacingRange" :text-size-open="showScalableRange"
							@tts="handleTtsOnClick" @original="handleTranslationOnClick"
							@spacing="handleSpacingIconOnClick" @text-size="handleScalableTextIconOnClick" />
			</div>
			<!-- 隱藏的音頻播放器 -->
			<audio ref="audioPlayer" :style="audioStyle" controls>
				<source ref="audioSourceElement" :src="audioSource" type="audio/mp3" />
			</audio>
			<!--  選擇語言的彈窗    -->
			<TranslationLanguageOverlay v-if="showLangOverlay"
										@change="handleSelectLangModalOnChange" @close="handleSelectLangModalOnClose" />
			<!--  文字大小滑軌    -->
			<RangeOverlay v-if="showScalableRange" v-model="fontSize" data-for="scale-text-button" :steps="FONT_SIZES"
						  icon-start="text-smaller" icon-end="text-larger" :label="t('Control.TextSize')"
						  @close="showScalableRange = false" />
			<!--  段距滑軌    -->
			<RangeOverlay v-if="showSpacingRange" v-model="paragraphGap" data-for="line-spacing-button"
						  :steps="GAP_STEPS" icon-start="spacing-tighter" icon-end="spacing-wider"
						  :label="t('Control.Spacing')" @close="showSpacingRange = false" />
			<!--   語音速度的彈窗   -->
			<SpeedOverlay v-if="showSpeechSpeedOverlay" v-model="speechSpeed" data-for="speech-speed-button"
						  @close="showSpeechSpeedOverlay = false" />
			<!--   QR Code 談窗   -->
			<ShareQRCodeModal :isOpen="showQRCodeModal"
							  @close="handleShareQRCodeModalOnClose" />
			<!--   匯出彈窗   -->
			<ExportOverlay v-if="showExportOverlay" :data="getTranscriptMessage" :summary="sessionSummary"
						   @close="handleExportOverlayOnClose" />
		</template>
	</DefaultLayout>
</template>
<script lang="ts">
import ControlBar from '../components/TutorialPage/ControlBar.vue'
import TranscriptPane, { type PaneItem, type TtsRevealClock } from '../components/TutorialPage/TranscriptPane.vue'
import DefaultLayout from '../layouts/DefaultLayout.vue'
import { computed, defineComponent, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { Channel, ErrorCodeEnum, ResponseDTO, Sessions, ShortUrl, Viewer } from '@shared-api'
import { MessageType, NotifyEnum, StorageKeyEnum } from '@shared-common'
import { getSupportedAzureSTTLanguages } from '@shared-data'
import { arrayIsEmpty, isEmpty, numberIsEmpty, stringIsEmpty } from '@ipevo-rd/service-web-utils'
import { LiveLabel, NavRightButton, RangeOverlay, SpeedOverlay } from '@shared-components'
import ShareQRCodeModal from '../components/TutorialPage/ShareQRCodeModal.vue'
import TranslationLanguageOverlay from '../components/TutorialPage/TranslationLanguageOverlay.vue'
import ExportOverlay from '../components/TutorialPage/ExportOverlay.vue'
import {
	buildStuckRecoveryAction,
	buildVisibilityResumePlan,
	makeHeadOfLineSkipper,
	makeStuckPlaybackWatchdog,
} from './ttsPlaybackHeal'
import { useInfoStore } from '@shared-store'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { v4 as uuidV4 } from 'uuid'

/**
 * 音檔 URL 收到後可以直接拿來播的最長時間（ms）；Worker 發的 SAS 一小時過期，留 10 分鐘緩衝，
 * 超過就當成沒有、交給輪詢向 API 重抓，避免點舊段時拿過期網址載入 403 而卡住
 */
const AUDIO_URL_MAX_AGE_MS = 50 * 60 * 1000

/**
 * 翻譯完成後延遲隱藏的時間，蓋掉句與句之間的停頓避免閃爍
 */
const INDICATOR_HIDE_DELAY_MS = 3000

/**
 * @interface PaneMessage 組視窗句子與挑音檔時用到的訊息欄位
 */
interface PaneMessage {
	/**
	 * 訊息 id
	 */
	message_id: string
	/**
	 * 講者名稱
	 */
	speaker?: string | null
	/**
	 * 原文
	 */
	original_text?: string
	/**
	 * 各語言的 LLM 譯文
	 */
	translated_text?: Record<string, string>
	/**
	 * 各語言的 app 同步譯文，優先於 LLM 譯文
	 */
	sync_text?: Record<string, string>
	/**
	 * 各語言的 TTS 音檔 URL；Worker 對所有語言廣播 tts_finish 且不重送，所以每個語言各存一格、換語言不清
	 */
	audioUrls?: Record<string, string>
	/**
	 * 各語言音檔 URL 的收到時間（ms），用來判斷 SAS 是否快過期
	 */
	audioUrlReceivedAt?: Record<string, number>
}

export default defineComponent({
	name: 'TutorialPage',
	components: {
		LiveLabel,
		NavRightButton,
		RangeOverlay,
		SpeedOverlay,
		TranslationLanguageOverlay,
		ShareQRCodeModal,
		TranscriptPane,
		ControlBar,
		DefaultLayout,
		ExportOverlay,
	},
	setup() {
		/**
		 * 字級六階，對應控制列 Text-size
		 */
		const FONT_SIZES = [12, 16, 20, 24, 28, 32]
		/**
		 * 段距五階，對應控制列 Spacing
		 */
		const GAP_STEPS = [4, 8, 12, 16, 20]
		/**
		 * 每個視窗最多渲染的段數，messages 仍全存
		 */
		const KEEP_PARAGRAPHS = 40
		/**
		 * Audio Player ref
		 */
		const audioPlayer = useTemplateRef<HTMLAudioElement>('audioPlayer')
		/**
		 * 播放器裡帶音檔 URL 的 <source>；URL 載入失敗（例如 SAS 過期 403）時 error 只發在這裡、不會傳到 <audio>
		 */
		const audioSourceElement = useTemplateRef<HTMLSourceElement>('audioSourceElement')
		/**
		 * 譯文視窗 ref，換語言時用來強制貼底
		 */
		const translationPane = useTemplateRef<{ forceBottom: () => void }>(
			'translationPane',
		)

		// 取得i18n
		const { t } = useI18n()
		// 取得路由
		const router = useRouter()
		// 使用 storeToRefs 來解構 Info Store 的狀態
		const { isDevMode, targetLang, meetTitle } = storeToRefs(useInfoStore())
		// 取得資訊存儲
		const {
			supportedTranslationLanguages,
			unsupportedTTSLanguages,
			shortID,
			sessionID,
			targetGender,
			setIsDevMode,
			setTargetLang,
			setMeetTitle,
			setShortID,
			setSessionID,
			setSupportedTranslationLanguages,
		} = useInfoStore()
		/**
		 * 是否為線上模式
		 */
		const isLive = ref(false)
		/**
		 * SSE 是否為重新連線
		 */
		const isSseReconnect = ref(false)
		/**
		 * SSE 斷線後自動重連的排程計時器，同一時間只存在一個
		 */
		const sseReconnectTimer = ref<ReturnType<typeof setTimeout> | null>(
			null,
		)
		/**
		 * 目前退避延遲毫秒數；初始 1s，指數成長（×2）上限 30s
		 */
		const sseBackoffMs = ref(1000)
		/**
		 * 連續 SSE 錯誤次數，達閾值時探測頻道存活
		 */
		const sseConsecutiveErrors = ref(0)
		/**
		 * 翻譯中指示器是否顯示，代表有句子正在翻譯
		 */
		const isTranslating = ref(false)
		/**
		 * 翻譯中指示器收到翻譯完成後排程中的隱藏計時器
		 */
		let indicatorHideTimer: ReturnType<typeof setTimeout> | null = null
		/**
		 * 會議摘要
		 */
		const sessionSummary = ref('')
		/**
		 * 是否顯示語音速度的overlay
		 */
		const showSpeechSpeedOverlay = ref(false)
		/**
		 * 語音速度
		 */
		const speechSpeed = ref(1)
		/**
		 * 是否顯示QR Code modal
		 */
		const showQRCodeModal = ref(false)
		/**
		 * 是否顯示文字放大縮小的彈窗
		 */
		const showScalableRange = ref(false)
		/**
		 * 是否顯示段距的彈窗
		 */
		const showSpacingRange = ref(false)
		/**
		 * 音源來源
		 */
		const audioSource = ref('')
		/**
		 * 當前播放到哪個音檔
		 */
		const currentVoiceIndex = ref<number | null>(null)
		/**
		 * 是否撥放完成
		 */
		const isVoicePlaying = ref(false)
		/**
		 * 是否顯示語言選擇的彈窗
		 */
		const showLangOverlay = ref(false)
		/**
		 * 對話紀錄
		 */
		const messages = ref<any[]>([])
		/**
		 * message_id → array index 的快取；避免 findIndex 線性搜尋，訊息量大時 O(1) 查找
		 */
		const messageIndexMap = new Map<string, number>()
		/**
		 * Font Size
		 */
		const fontSize = ref(16)
		/**
		 * 段落間距
		 */
		const paragraphGap = ref(8)
		/**
		 * 是否為不撥放狀態
		 */
		const isMuted = ref(true)
		/**
		 * 是否顯示 TTS 視窗；與靜音脫鉤，音檔仍等使用者點過才播
		 */
		const showTts = ref(false)
		/**
		 * 是否顯示原文視窗
		 */
		const showOriginText = ref(false)
		/**
		 * SSE 連線
		 */
		const sse = ref<EventSource | null>(null)
		/**
		 * Banner點擊次數
		 */
		const bannerClickCount = ref(0)
		/**
		 * Banner點擊計時器
		 */
		const bannerClickTimer = ref<ReturnType<typeof setTimeout> | null>(null)
		/** sessionStorage 不可用時的記憶體 fallback viewer ID，分頁存活期間穩定 */
		let _viewerIdFallback: string | null = null
		/**
		 * 取得穩定 Viewer ID（sessionStorage 持久化，分頁存活期間跨重連不變）
		 */
		const getOrCreateViewerId = (): string => {
			try {
				// 嘗試從 sessionStorage 讀取已存的 viewer_id
				const stored = sessionStorage.getItem(StorageKeyEnum.ViewerId)
				// 已有合法 uuid 直接回傳
				if (stored && stored.length > 0) return stored
				// 產新的 uuid v4 並存起來
				const newId = uuidV4()
				sessionStorage.setItem(StorageKeyEnum.ViewerId, newId)
				return newId
			} catch {
				// sessionStorage 不可用（隱私模式/WebView 限制）時走記憶體 fallback
				if (!_viewerIdFallback) {
					// 首次取不到時建立並記住，分頁存活期間跨重連不變
					_viewerIdFallback = uuidV4()
				}
				return _viewerIdFallback
			}
		}
		/**
		 * 穩定 Viewer ID；初始化時從 sessionStorage 取或建立，分頁存活期間一致
		 */
		const viewerID = ref(getOrCreateViewerId())
		/** 追蹤 renew heartbeat interval handle，用於清除 */
		let renewInterval: ReturnType<typeof setInterval> | null = null
		/**
		 * 啟動 viewer renew heartbeat，每 30s 呼叫一次 renewViewer 更新存活 timestamp
		 *
		 * @returns {void}
		 *
		 * @example
		 * startRenewHeartbeat()
		 */
		const startRenewHeartbeat = (): void => {
			// 清除既有 interval，防止重複啟動疊加多個
			if (renewInterval !== null) {
				clearInterval(renewInterval)
			}
			renewInterval = setInterval(() => {
				// 靜默忽略網路錯誤，renew 失敗不影響 SSE 主流程
				Sessions.User.renewViewer(sessionID, viewerID.value).catch(
					() => {
					},
				)
			}, 30000)
		}
		/**
		 * 停止 viewer renew heartbeat，清除 interval
		 *
		 * @returns {void}
		 *
		 * @example
		 * stopRenewHeartbeat()
		 */
		const stopRenewHeartbeat = (): void => {
			// 清除 interval 並重置 handle
			if (renewInterval !== null) {
				clearInterval(renewInterval)
				renewInterval = null
			}
		}
		/**
		 * Audio Player的樣式
		 * @return {StyleValue}
		 */
		const audioStyle = computed(() =>
			isDevMode.value
				? {
					position: 'fixed',
					bottom: '80px',
					left: '50%',
					transform: 'translateX(-50%)',
					zIndex: '999',
					width: '90%',
					maxWidth: '500px',
					backgroundColor: '#fff',
					border: '2px solid #333',
					borderRadius: '8px',
					boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
				}
				: { display: 'none' },
		)
		/**
		 * 取得只有逐字稿的訊息
		 * @return {Array<any>}
		 */
		const getTranscriptMessage = computed(() =>
			messages.value.filter(
				(message) => message.type === MessageType.TranscriptMessage,
			),
		)
		/**
		 * 顯示播放語音按鈕
		 * @return {boolean}
		 */
		const showVoiceButton = computed(
			() => !unsupportedTTSLanguages.includes(targetLang.value),
		)
		/**
		 * 是否顯示匯出的Overlay
		 */
		const showExportOverlay = ref(false)
		/**
		 * 講者清單
		 */
		const speakers = ref<unknown[]>([])
		/**
		 * TTS是否啟用
		 */
		const isTTSActive = ref(true)
		/**
		 * 頻道與目標語言都支援 TTS；不支援就沒有 TTS 視窗，也不給控制列的 TTS 項目
		 */
		const ttsAvailable = computed(
			() => isTTSActive.value && showVoiceButton.value,
		)
		/**
		 * 是否顯示 TTS 視窗
		 */
		const showTtsPane = computed(
			() => ttsAvailable.value && showTts.value,
		)
		/**
		 * 是否顯示視窗標題列；只有一個視窗時沒有東西要辨識，不給標題列
		 */
		const showPaneHead = computed(
			() => showTtsPane.value || showOriginText.value,
		)
		/**
		 * 頻道是否有語者分離；沒有的話整個語者欄消失
		 */
		const showSpeakers = computed(() => speakers.value.length > 0)
		/**
		 * 取得訊息在目標語言的譯文：sync_text 優先，其次 translated_text
		 * @param message {any} 訊息
		 * @return {string}
		 */
		const translationOf = (message: PaneMessage): string =>
			message.sync_text?.[targetLang.value] ||
			message.translated_text?.[targetLang.value] ||
			''
		/**
		 * 把 messages 轉成視窗要渲染的句子：由後往前挑出有文字的句子，最多 40 段
		 * @param textOf {Function} 取得該視窗要顯示的文字
		 * @param lastIndex {number} 只挑到這個索引（含），TTS 視窗只長到正在唸的那一段
		 * @return {{ items: PaneItem[], hasMore: boolean }} 句子清單與前面是否還有被截掉的
		 */
		const buildPaneState = (
			textOf: (message: PaneMessage) => string,
			lastIndex: number = messages.value.length - 1,
		): { items: PaneItem[]; hasMore: boolean } => {
			// 由後往前挑，多挑一句當第一句的前一句，用來判斷是否連續同語者
			const picked: { message: PaneMessage; index: number; text: string }[] = []
			for (
				let index = Math.min(lastIndex, messages.value.length - 1);
				index >= 0 && picked.length < KEEP_PARAGRAPHS + 1;
				index--
			) {
				// 取得該視窗的文字
				const text = textOf(messages.value[index])
				// 還沒有文字的句子不進視窗
				if (!stringIsEmpty(text)) {
					picked.unshift({ message: messages.value[index], index, text })
				}
			}

			// 挑滿 41 句代表前面還有被截掉的
			const hasMore = picked.length > KEEP_PARAGRAPHS
			// 多挑的那一句只當前一句，不渲染
			const visible = hasMore ? picked.slice(1) : picked
			// 語者名稱：空與 unknown 一律當作沒有語者
			const speakerOf = (message: PaneMessage): string =>
				stringIsEmpty(message.speaker) ||
				message.speaker.trim().toLowerCase() === 'unknown'
					? ''
					: message.speaker

			return {
				items: visible.map((item, position) => {
					// 前一句：視窗內的上一句，第一句用多挑的那一句
					const previous = hasMore
						? picked[position]
						: picked[position - 1]
					const speaker = speakerOf(item.message)

					return {
						key: item.message.message_id,
						index: item.index,
						speaker,
						speakerNo: speakers.value.indexOf(speaker) + 1,
						text: item.text,
						isContinued:
							!!previous && speakerOf(previous.message) === speaker,
					}
				}),
				hasMore,
			}
		}
		/**
		 * 原文視窗的句子
		 */
		const sourcePaneState = computed(() =>
			buildPaneState((message) => message.original_text ?? ''),
		)
		/**
		 * 譯文視窗的句子
		 */
		const translationPaneState = computed(() =>
			buildPaneState(translationOf),
		)
		/**
		 * TTS 視窗的句子，只長到 currentVoiceIndex
		 */
		const ttsPaneState = computed(() =>
			buildPaneState(translationOf, currentVoiceIndex.value ?? -1),
		)
		/**
		 * TTS 是否落後譯文；落後才能按 Go to Latest
		 */
		const isTtsBehind = computed(() => {
			// 譯文視窗最新一句
			const items = translationPaneState.value.items
			const latest = items.length > 0 ? items[items.length - 1].index : -1

			return latest > (currentVoiceIndex.value ?? -1)
		})
		/**
		 * TTS 逐行揭露的更新間隔（ms）
		 */
		const TTS_TICK_MS = 80
		/**
		 * 最近一次從 audio 讀到的播放進度；只有音檔播放中才會更新
		 */
		const ttsClock = ref<TtsRevealClock | null>(null)
		/**
		 * 讀取播放進度的 setInterval handle；音檔開始播才建立，停了或卸載就清掉
		 */
		let ttsTickTimer: ReturnType<typeof setInterval> | null = null
		/**
		 * 交給 TTS 視窗的揭露進度：靜音時整段顯示（null），已取消靜音但音檔還沒開始播時從頭揭露，播放中吃 audio 真實進度
		 */
		const ttsRevealClock = computed<TtsRevealClock | null>(() => {
			// 靜音時不吃進度，整段顯示
			if (isMuted.value) return null
			// 等音檔時從頭揭露
			if (!isVoicePlaying.value) return { currentTime: 0, duration: 1 }

			return ttsClock.value
		})
		/**
		 * 輪詢是否正在執行中；防止同一輪尚未完成時重入
		 */
		const voicePollingInProgress = ref(false)
		/**
		 * 輪詢世代編號；停播、跳播、回前景重置輪詢鎖時加一，讓上一代還在飛的 Mp3Url 回應與冷卻解鎖作廢
		 */
		let voicePollingGeneration = 0
		/**
		 * 播放世代編號；每次 playMessage 加一，被新一段打斷的舊 play() 被拒時才不會清掉新一段的播放旗標
		 */
		let voicePlayGeneration = 0
		/**
		 * 音檔輪詢的 setInterval 計時器 handle；unmute 時建立，mute / 離開頁面時 clearInterval
		 */
		const audioPlayInterval = ref<ReturnType<typeof setInterval> | null>(
			null,
		)
		/**
		 * 卡死播放看門狗：OS 鎖屏強制 pause 只發 pause 不發 ended/error，靠連續 3 次（約 1.5s）偵測解除卡死旗標
		 */
		const stuckPlaybackWatchdog = makeStuckPlaybackWatchdog(3)
		/**
		 * head-of-line 逾時偵測器：隊首訊息 15s 內等不到音檔就跳過，避免卡住後續訊息
		 */
		const headOfLineSkipper = makeHeadOfLineSkipper(15000)
		/**
		 * 15 秒跳過已在哪一句開始緩衝時重新起算；同一句載入失敗後 API 立刻又給網址、再次載入時不再重置，否則一直載不起來的那句永遠跳不過去。
		 * 輪詢問到伺服器還沒有網址時清掉（等伺服器生音檔的時間不算緩衝時間），停播、點段跳播、回前景時也清掉，讓使用者主動操作後能重新起算
		 */
		let skipperResetIndex: number | null = null
		/**
		 * 最近一次掛在 audioPlayer 上的 canplaythrough handler；playMessage 重入或跳到最新一筆時用來移除舊的，避免背景輪詢重複呼叫或跳最新後被放棄的舊句 buffer 完成時仍然播出來
		 */
		let pendingCanPlayThroughHandler: (() => Promise<void>) | null = null
		/**
		 * 最近一次收到 SSE 事件的時間戳（ms）；open / notify / ping 三者都會更新
		 */
		const lastSseEventAt = ref<number>(0)
		/**
		 * 最後一次收到的 SSE event id（nchan etag）；重連時帶 ?last_event_id= 讓 nchan 從 buffer 補發斷線期間訊息
		 */
		const lastSseEventId = ref<string>('')
		/**
		 * 殭屍連線看門狗的 setInterval handle；connect() 啟動，unmount / ChannelReplace 清除
		 */
		const sseWatchdogTimer = ref<ReturnType<typeof setInterval> | null>(
			null,
		)
		/**
		 * 取得訊息在目前翻譯語言的音檔 URL；音檔以語言為 key 存在 audioUrls，換語言時各語言的 URL 都留著
		 * @param message {PaneMessage | null | undefined} 訊息
		 * @return {string | undefined} 目前語言的音檔 URL，還沒拿到或已快過期時為 undefined
		 */
		const audioUrlOf = (
			message: PaneMessage | null | undefined,
		): string | undefined => {
			// 取得目前語言的音檔 URL 與收到時間
			const url = message?.audioUrls?.[targetLang.value]
			const receivedAt = message?.audioUrlReceivedAt?.[targetLang.value]

			// 存太久的 SAS 快過期了，當成沒有，輪詢才會向 API 換一個新的
			if (receivedAt !== undefined && Date.now() - receivedAt > AUDIO_URL_MAX_AGE_MS) {
				return undefined
			}

			return url
		}

		/**
		 * 嘗試播放當前 index 的音檔；由 tts_finish SSE 或 handleAudioOnEnded 觸發
		 * @return {void}
		 */
		const tryPlayCurrentVoice = (): void => {
			// 靜音中不播
			if (isMuted.value) return
			// 正在播放中不重複觸發
			if (isVoicePlaying.value) return
			// index 越界不播
			if (
				numberIsEmpty(currentVoiceIndex.value) ||
				currentVoiceIndex.value! < 0 ||
				currentVoiceIndex.value! >= messages.value.length
			)
				return

			// 取得當前訊息
			const message = messages.value[currentVoiceIndex.value!]
			// 訊息為空不播
			if (isEmpty(message)) return
			// 非逐字稿跳下一筆
			if (message.type !== MessageType.TranscriptMessage) {
				currentVoiceIndex.value = currentVoiceIndex.value! + 1
				return
			}

			// 取得目前語言的音檔 URL
			const audioUrl = audioUrlOf(message)
			// 音檔 URL 還沒到，等 tts_finish SSE 或輪詢拿到時會再觸發
			if (!audioUrl) return
			// 同一個網址已在緩衝、等 canplaythrough 中，重新 load 會把慢網路下已下載的進度丟掉，交給 canplaythrough 接手
			if (isBufferingUrl(audioUrl)) return

			// 播放目前語言的音檔
			playMessage(audioUrl)
		}

		/**
		 * 判斷指定網址是否正在緩衝、等 canplaythrough 才開始播
		 * @param url {string} 要比對的音檔 URL
		 * @return {boolean} 已掛著 canplaythrough handler 且音源就是這個網址時為 true
		 */
		const isBufferingUrl = (url: string): boolean =>
			pendingCanPlayThroughHandler !== null && audioSource.value === url

		/**
		 * 取消尚未觸發的 canplaythrough listener；playMessage 重入與 visibilitychange 跳到最新一筆前都要先清掉舊的，
		 * 否則被放棄的舊句 buffer 完成時仍會播出來，播完 ended handler 再 +1 把最新那筆整筆跳過
		 * @return {void}
		 */
		const cancelPendingCanPlayThrough = (): void => {
			// 尚未掛過待處理的 handler，沒有東西可取消
			if (!pendingCanPlayThroughHandler) return
			// 取消訂閱，避免之後才觸發時呼叫到已作廢的 handler
			audioPlayer.value?.removeEventListener(
				'canplaythrough',
				pendingCanPlayThroughHandler,
			)
			// 清空追蹤，避免重複移除或誤判仍有待處理的 handler
			pendingCanPlayThroughHandler = null
		}

		/**
		 * 播放指定 URL 的音檔
		 * @param url {string} 音檔 URL
		 * @return {void}
		 */
		const playMessage = (url: string): void => {
			// 設定音檔 URL 並載入
			audioSource.value = url
			audioPlayer.value?.load()

			// 取得播放器
			const player = audioPlayer.value as HTMLAudioElement

			// 背景輪詢可能在舊的 canplaythrough 尚未觸發前重入呼叫 playMessage，先移除舊 handler 避免疊加
			cancelPendingCanPlayThrough()
			// 記下這次播放的世代，play() 被拒時用來判斷是不是已經被新一段取代
			const playGeneration = ++voicePlayGeneration

			/**
			 * 嘗試播放
			 * @return {Promise<void>}
			 */
			const tryPlay = async (): Promise<void> => {
				setIsVoicePlaying(true)
				try {
					player.playbackRate = speechSpeed.value
					player.preservesPitch = true
					await player.play()
				} catch (err) {
					console.warn('播放失敗:', err)
					// 跳播或換語言會 pause 掉還沒開始的舊 play()，這時旗標屬於正在播的新一段，不能清
					if (playGeneration !== voicePlayGeneration) return
					// 這次播放本身被拒，還原旗標交給輪詢重試
					setIsVoicePlaying(false)
				}
			}

			// 播放器已有足夠資料時直接播
			if (player.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
				tryPlay()
			} else {
				// 等緩衝完再播
				const onCanPlayThrough = async (): Promise<void> => {
					pendingCanPlayThroughHandler = null
					await tryPlay()
				}
				pendingCanPlayThroughHandler = onCanPlayThrough
				player.addEventListener('canplaythrough', onCanPlayThrough, { once: true })
				// 15 秒跳過只在這一句第一次開始緩衝、或伺服器補出網址後開始緩衝時重新起算：等網址花掉的時間不能算進緩衝時間，
				// 但網址一直拿得到、只是每次載入都失敗時不能再重置，否則那句永遠等不到 15 秒
				if (currentVoiceIndex.value !== skipperResetIndex) {
					skipperResetIndex = currentVoiceIndex.value
					headOfLineSkipper.reset()
				}
			}
		}

		/**
		 * 輪詢自癒迴圈：每輪檢查當前 index 的音檔是否備妥，未備妥則向 API 查詢 SAS URL
		 * @return {Promise<void>}
		 */
		const startVoicePolling = async (): Promise<void> => {
			// 上一輪仍在執行中或正在播放中，跳過此輪以防重入
			if (voicePollingInProgress.value || isVoicePlaying.value) return

			// 標記輪詢執行中
			voicePollingInProgress.value = true
			// 記下這一輪所屬的世代，回應或冷卻結束時世代已變就代表被停播或換語言取代
			const pollingGeneration = voicePollingGeneration

			/**
			 * 延遲 ms 毫秒的 Promise，供 async/await 冷卻用途
			 * @param ms {number}
			 * @return {Promise<void>}
			 */
			const delay = (ms: number): Promise<void> =>
				new Promise((resolve) => setTimeout(resolve, ms))

			try {
				// index 越界或靜音中不執行
				if (
					numberIsEmpty(currentVoiceIndex.value) ||
					currentVoiceIndex.value! < 0 ||
					currentVoiceIndex.value! >= messages.value.length ||
					isMuted.value
				)
					return

				// 取得當前訊息
				const message = messages.value[currentVoiceIndex.value!]
				if (isEmpty(message)) return

				// 非逐字稿跳下一筆
				if (message.type !== MessageType.TranscriptMessage) {
					currentVoiceIndex.value = currentVoiceIndex.value! + 1
					return
				}

				// 目前語言已有音檔：直接走 tryPlayCurrentVoice 快路徑
				if (audioUrlOf(message)) {
					tryPlayCurrentVoice()
					return
				}

				// 目前語言的音檔尚未到達：向 API 查詢 blob 是否存在並取得 SAS URL
				const msgId = message.message_id
				if (!msgId) return
				// 記下這次查的是哪個語言，回應寫回時才會落在正確的那一格
				const requestLang = targetLang.value

				console.debug('[voice polling] fetching audio url', {
					index: currentVoiceIndex.value,
					msgId,
				})

				// 音檔還沒準備好時 API 可能回 4xx；包 try/catch 避免變成 unhandled rejection，失敗就等下一輪自然重試
				try {
					const res = (await Channel.User.Mp3Url({
						id: sessionID,
						language: requestLang,
						gender: targetGender,
						message_id: msgId,
					})) as any

					// 請求期間停播、跳播或換了語言，這個回應可能是舊語言的音檔，直接丟掉
					if (pollingGeneration !== voicePollingGeneration) return

					// API 回 code=0 且帶 url 時寫進該語言那一格並播放
					if (res?.code === 0 && res?.data?.url) {
						const idx = currentVoiceIndex.value!
						if (
							idx >= 0 &&
							idx < messages.value.length &&
							messages.value[idx].message_id === msgId
						) {
							messages.value[idx] = {
								...messages.value[idx],
								audioUrls: {
									...messages.value[idx].audioUrls,
									[requestLang]: res.data.url,
								},
								// 記下收到時間，存太久時才知道要重抓
								audioUrlReceivedAt: {
									...messages.value[idx].audioUrlReceivedAt,
									[requestLang]: Date.now(),
								},
							}
							tryPlayCurrentVoice()
						}
					} else {
						console.debug('[voice polling] audio not ready yet', {
							msgId,
							code: res?.code,
						})
						// 伺服器還沒有音檔，之後補出網址開始緩衝時 15 秒要重新起算，等它生音檔的時間不算緩衝時間
						skipperResetIndex = null
					}
				} catch (err) {
					console.debug('[voice polling] fetch audio url failed', {
						msgId,
						err,
					})
					// 拿不到網址跟伺服器還沒有音檔一樣，之後拿到網址開始緩衝時 15 秒要重新起算
					skipperResetIndex = null
				}
			} finally {
				// 無論成功失敗，冷卻 2.5s 再解鎖，避免高頻重打 API
				await delay(2500)
				// 世代已變時鎖已由新一代接手，舊一輪不能提早解開，否則兩輪輪詢會重疊
				if (pollingGeneration === voicePollingGeneration) {
					voicePollingInProgress.value = false
				}
			}
		}

		/**
		 * 500ms 輪詢 interval 的實際 callback；依序跑卡死看門狗自癒、head-of-line 逾時跳過、輪詢自癒三件事
		 * @return {void}
		 */
		const runVoiceTick = (): void => {
			// 頁面隱藏中時 OS 強制 pause 的行為會讓看門狗誤判卡死、head-of-line 也可能誤跳，兩段只在頁面可見時執行
			const isPageVisible = !document.hidden

			// 僅頁面可見時才跑看門狗自癒與 head-of-line 跳過；背景時維持既有訊息位置不誤跳
			if (isPageVisible) {
				// 看門狗偵測到旗標卡在播放中但 player 實際已停時，回報應該執行自癒
				const isStuck = stuckPlaybackWatchdog.tick({
					isVoicePlaying: isVoicePlaying.value,
					playerPaused: audioPlayer.value?.paused ?? true,
					playerEnded: audioPlayer.value?.ended ?? false,
				})

				// 偵測到卡死時，依當下 player 狀態判定要原地續播還是交還輪詢排程
				if (isStuck) {
					// 判定卡死自癒動作：有播放進度可續播就地 resume，否則重置旗標交還輪詢
					const action = buildStuckRecoveryAction({
						hasSource: !!audioSource.value,
						playerEnded: audioPlayer.value?.ended ?? false,
						currentTimeStarted:
							(audioPlayer.value?.currentTime ?? 0) > 0,
					})

					// 有可續播的進度：原地呼叫 play() 續播，不重讀不重播整句
					if (action === 'resume-play') {
						// flag 保持 true、播完由既有 ended handler 收尾
						const resumeGeneration = voicePlayGeneration
						audioPlayer.value?.play().catch(() => {
							// 續播期間跳播或換語言已換成新一段，旗標屬於新一段不能清
							if (resumeGeneration !== voicePlayGeneration) return
							// 續播本身被拒才重置旗標交還輪詢排程
							setIsVoicePlaying(false)
						})
					} else {
						// 沒有可續播的進度（無音源 / 已播畢 / 尚未真正開始播放），重置卡死旗標交還輪詢排程
						setIsVoicePlaying(false)
					}
				}

				// 判斷目前 index 是否指向合法、且缺目前語言音檔或音檔一直緩衝不完的逐字稿訊息
				const idx = currentVoiceIndex.value
				const isLegalIndex =
					idx !== null && idx >= 0 && idx < messages.value.length
				const message = isLegalIndex ? messages.value[idx!] : null
				// 取得目前語言的音檔 URL，緩衝卡住時要用它比對
				const currentAudioUrl = audioUrlOf(message)
				// 緩衝中不重新 load，載入卡住又不發 error 時只能靠隊首 15 秒跳過脫身
				const isEligibleForSkip =
					!isMuted.value &&
					!isVoicePlaying.value &&
					isLegalIndex &&
					!isEmpty(message) &&
					message.type === MessageType.TranscriptMessage &&
					(!currentAudioUrl || isBufferingUrl(currentAudioUrl))

				// 依是否符合跳過條件餵給偵測器；不符合時帶 index: null 清除追蹤，避免殘留舊的起算點
				const shouldSkip = isEligibleForSkip
					? headOfLineSkipper.tick({
						index: idx,
						hasAudioUrl: false,
						nowMs: Date.now(),
					})
					: headOfLineSkipper.tick({
						index: null,
						hasAudioUrl: true,
						nowMs: Date.now(),
					})

				// 逾時仍等不到音檔，跳過此筆並嘗試播放下一筆
				if (shouldSkip) {
					// 取消被跳過那筆的 canplaythrough，晚到時才不會播出舊句或擋住下一筆
					cancelPendingCanPlayThrough()
					currentVoiceIndex.value = currentVoiceIndex.value! + 1
					tryPlayCurrentVoice()
				}
			}

			// 觸發輪詢自癒；背景時也照跑，維持既有背景行為不變
			startVoicePolling()
		}

		/**
		 * 回報數據
		 * @return {void}
		 */
		const report = (): void => {
			//呼叫回報API
			Viewer.report({
				session_id: sessionID,
				viewer_id: viewerID.value,
				language: targetLang.value,
			}).then((_) => {
			})
		}

		/**
		 * 避免螢幕進入休眠
		 * @return {Promise<void>}
		 */
		const requestWakeLock = async (): Promise<void> => {
			try {
				await navigator.wakeLock.request('screen')
				console.log('✅ Wake Lock 已啟用')
			} catch (err) {
				console.error('❌ 無法啟用 Wake Lock:')
			}
		}

		/**
		 * 透過 nchan SSE 直連
		 * - viewer_id 從 sessionStorage 取穩定 UUID，不存在時建立後存回
		 * - 連線錯誤觸發指數退避重連（1s 起、×2、上限 30s、±30% jitter）
		 * @async
		 * @returns {Promise<void>}
		 */
		const connect = async (): Promise<void> => {
			// 先關掉舊連線，避免 reconnect 時累積多條 SSE 連線導致前端卡頓
			if (sse.value) {
				sse.value.close()
				sse.value = null
			}

			// 從 sessionStorage 取穩定 viewer_id，nchan 依此計算 unique viewer 數
			viewerID.value = getOrCreateViewerId()
			// nchan SSE 直連（path 對齊現行 viewer，nchan 鏡射了同一路由）
			sse.value = Sessions.User.connectNchan({
				channel_id: sessionID,
				viewer_id: viewerID.value,
				// 重連時帶上最後 event id，向 nchan 要求補發斷線期間 buffer 內的訊息（避免漏訊息）
				last_event_id: lastSseEventId.value || undefined,
			})

			// SSE 連線開啟時
			sse.value.addEventListener('open', handleSseOnOpen)
			// 接收到 notify 訊息時
			sse.value.addEventListener('notify', handleSseOnMessage)
			// 接收到後端心跳 ping 時，只更新看門狗時間戳，data 為 {} 不需解析
			sse.value.addEventListener('ping', ((event: MessageEvent) => {
				lastSseEventAt.value = Date.now()
				// ping 若帶 nchan event id 一併更新，避免重連補發起點過舊
				if (event.lastEventId) {
					lastSseEventId.value = event.lastEventId
				}
			}) as EventListener)
			// 連線錯誤時完全接管退避：強制 close 後按指數退避排程重連
			// 原因：瀏覽器原生 EventSource retry（CONNECTING 狀態）無退避 / jitter 控制
			// nchan 過載拒連時若讓瀏覽器自動 retry 會造成 retry storm，因此一律由我們排程
			sse.value.addEventListener('error', () => {
				// 停止 renew heartbeat，等重連成功後再啟動
				stopRenewHeartbeat()
				// 強制關閉現有連線，取消瀏覽器原生自動重連
				sse.value?.close()
				sse.value = null
				// 防止 error 連發疊多個 timer
				if (sseReconnectTimer.value !== null) {
					clearTimeout(sseReconnectTimer.value)
				}
				// 累計連續錯誤次數
				sseConsecutiveErrors.value += 1
				// 達到閾值時探測頻道是否仍存活，避免對已消失的頻道無限退避
				if (sseConsecutiveErrors.value >= 5) {
					;(async () => {
						try {
							const res = (await ShortUrl.getOriginalURL({
								url: shortID,
							})) as unknown as ResponseDTO
							// 頻道仍存活，重置計數後繼續退避
							if (res.code === ErrorCodeEnum.Success) {
								sseConsecutiveErrors.value = 0
							} else {
								// 頻道確認消失，停止所有重連並跳轉結束頁
								clearTimeout(sseReconnectTimer.value!)
								sseReconnectTimer.value = null
								clearInterval(sseWatchdogTimer.value!)
								sseWatchdogTimer.value = null
								sse.value = null
								router.push({ name: 'meetingEnd' })
							}
						} catch {
							// 探測本身網路錯誤，繼續退避（計數不重置讓下次再探測）
						}
					})()
				}
				// 計算帶 ±30% jitter 的退避時間，避免大量 viewer 同秒重連擊中同一台 nchan
				const jitter = sseBackoffMs.value * (Math.random() * 0.6 - 0.3)
				const delay = sseBackoffMs.value + jitter
				console.warn(
					'[SSE] connection error, scheduling backoff reconnect in',
					Math.round(delay),
					'ms',
				)
				sseReconnectTimer.value = setTimeout(() => {
					sseReconnectTimer.value = null
					connect()
				}, delay)
				// 指數退避，上限 30s
				sseBackoffMs.value = Math.min(sseBackoffMs.value * 2, 30000)
			})

			// 看門狗啟動：每 10s 輪詢一次，只對「已連上但靜默超過 45s」的殭屍連線強制重連
			// OPEN → 看門狗負責；CONNECTING → 不介入；CLOSED → error handler 負責
			// 只啟動一個，防止 connect() 被重複呼叫時累積多個 interval
			if (sseWatchdogTimer.value === null) {
				// 初始化時間戳，避免啟動瞬間因 lastSseEventAt 為 0 誤觸發
				lastSseEventAt.value = Date.now()
				sseWatchdogTimer.value = setInterval(() => {
					if (sse.value === null) return
					if (sse.value.readyState !== EventSource.OPEN) return
					if (Date.now() - lastSseEventAt.value > 45000) {
						console.warn(
							'[SSE watchdog] no event for 45s (readyState: OPEN), forcing reconnect',
						)
						connect()
					}
				}, 10000)
			}
		}

		/**
		 * 音檔載入或播放失敗時只清掉目前語言的音檔，讓輪詢下一輪重新取 SAS URL（SAS 過期自癒）；別語言的 URL 留著換過去才不用重抓
		 * @return {void}
		 */
		const handleAudioOnError = (): void => {
			// 載入已失敗，不會再等到 canplaythrough；取消掛著的 handler，重抓回同一個網址時才會重新 load
			cancelPendingCanPlayThrough()
			// 記下出錯當下指的是哪一筆，錯誤只歸到這一筆
			const idx = currentVoiceIndex.value
			// 索引越界或已被清空時沒有訊息可清，只還原播放旗標
			if (
				idx !== null &&
				idx >= 0 &&
				idx < messages.value.length
			) {
				// 留下出錯的筆數與語言，追 SAS 過期或 403 時才對得上是哪一段
				console.debug(
					'[audio error] clearing audio url of current language to trigger re-fetch',
					{ idx, language: targetLang.value },
				)
				// 複製一份目前的音檔表，其他語言不動
				const audioUrls = { ...messages.value[idx].audioUrls }
				// 拿掉目前語言那一格，輪詢才會重抓
				delete audioUrls[targetLang.value]
				// 整筆換成新物件寫回，觸發畫面與輪詢看得到網址已清掉
				messages.value[idx] = {
					...messages.value[idx],
					audioUrls,
				}
			}
			// 這次播放已失敗，旗標還原成沒在播，輪詢下一輪才會重抓並重播
			setIsVoicePlaying(false)
		}

		/**
		 * 初始化Audio Player
		 * @return {void}
		 */
		const initAudioPlayer = (): void => {
			// 如果Audio Player存在
			if (audioPlayer.value) {
				// 設定音樂撥放完成後的監聽事件
				audioPlayer.value!.addEventListener('ended', handleAudioOnEnded)
				// 播放中解碼失敗等錯誤發在 <audio> 上
				audioPlayer.value!.addEventListener('error', handleAudioOnError)
			}
			// URL 載入失敗（例如 SAS 過期 403）只發在 <source> 上、不會傳到 <audio>，要另外掛才收得到
			audioSourceElement.value?.addEventListener('error', handleAudioOnError)
		}

		/**
		 * 初始化
		 * @async
		 * @return {Promise<void>}
		 */
		const init = async (): Promise<void> => {
			// 用於檢查人數上限
			await checkSessionLimit()
			// 初始化撥放器
			initAudioPlayer()
			//避免螢幕進入休眠
			await requestWakeLock()
			// 發送數據
			report()
			// 連接 Web Socket
			connect()
			// 配置除錯模式
			configureDebug()
			// 設定標題
			document.title = `${document.title}-${meetTitle.value}`
		}

		/**
		 * 檢查會議人數上限
		 * @async
		 * @param enforceLimit {boolean} 是否在滿員時踢回首頁，重連同步時傳 false
		 * @return {Promise<void>}
		 */
		const checkSessionLimit = async (enforceLimit = true): Promise<void> => {
			// 如果沒有短網址ID
			const res = (await ShortUrl.getOriginalURL({
				url: shortID,
			})) as unknown as ResponseDTO

			// 取得成功的結果
			if (res.code === ErrorCodeEnum.Success) {
				// 取得 最大人數 / 當前人數 / 頻道標題 / 翻譯語言清單 / 是否啟用TTS / 是否直播中 / 講者顏色索引
				const {
					size,
					capacity,
					title,
					languages,
					is_tts_active = true,
					is_on_air,
					speaker_color_index,
				} = res.data as Record<string, any>
				// 設定標題
				setMeetTitle(title)
				// 同步翻譯語言清單，讓 reset 改語言後已連線的 viewer 也拿到新清單
				if (Array.isArray(languages)) {
					setSupportedTranslationLanguages(languages)
				}
				// 設定TTS啟用狀態
				isTTSActive.value = is_tts_active
				// 連線前設定直播狀態，讓 isLive 連 SSE 前就正確顯示
				isLive.value = is_on_air ?? false
				// 連線前初始化講者清單；後續 speaker_update SSE 事件還是會持續更新
				if (speakers.value.length === 0 && speaker_color_index) {
					speakers.value = (
						Object.entries(speaker_color_index) as [
							string,
							number,
						][]
					)
						.filter(([name]) => name !== 'Unknown')
						.sort((a, b) => a[1] - b[1])
						.map(([name]) => name)
				}
				// 如果人數達到上限
				if (enforceLimit && size >= capacity) {
					// 顯示錯誤訊息
					window.$toast?.({
						type: 'error',
						message: t('Tips.Error.Channel.LimitExceeded'),
					})
					// 跳轉至首頁
					await router.push({ name: 'home' })
				}
			}
		}

		/**
		 * 配置除錯模式
		 * @return {void}
		 */
		const configureDebug = (): void => {
			// 取得Logo Div
			const logo = document.getElementById('logo') as HTMLElement | null
			// 如果Logo存在
			if (logo) {
				logo.addEventListener('click', handleLogoOnClick)
			}
		}

		/**
		 * 取得摘要
		 * @return {void}
		 */
		const getSummary = (): void => {
			Sessions.User.getSessionSummary({
				session_id: sessionID,
				language: targetLang.value,
			}).then((res) => {
				// 取得代碼和資料
				const { code, data } = res as unknown as ResponseDTO
				// 執行正確時
				if (code === ErrorCodeEnum.Success) {
					// 取得摘要
					const summary = data!.summary as string
					// 如果摘要不為空
					if (!stringIsEmpty(summary)) {
						// 將摘要設定到會議摘要中
						sessionSummary.value = summary
					}
				}
			})
		}

		/**
		 * 立即隱藏翻譯中指示器並取消排程中的隱藏，下播、重連同步、卸載與計時到期都走這裡
		 * @return {void}
		 */
		const hideIndicator = (): void => {
			// 取消排程中的隱藏，避免卸載或下播後計時器還去改狀態
			if (indicatorHideTimer !== null) {
				// 清掉隱藏計時器
				clearTimeout(indicatorHideTimer)
				// 清空參照，下次翻譯完成才會重新排程
				indicatorHideTimer = null
			}
			// 隱藏翻譯中指示器
			isTranslating.value = false
		}

		/**
		 * 下播時立即隱藏翻譯中指示器，通知與重連同步都只改 isLive
		 * 用 sync flush 讓每次轉換都當下處理，同一個 tick 內下播又上播時才不會被合併成沒有變化
		 * 目前 SSE 一則訊息只帶一個通知，同一 tick 內的連續轉換在正式環境走不到，flush: 'sync' 是防禦性選擇
		 */
		watch(
			isLive,
			(now) => {
				// 下播後畫面上不該再出現指示器，立即隱藏並取消排程中的隱藏
				if (now === false) {
					// 隱藏指示器並取消隱藏計時
					hideIndicator()
				}
			},
			{ flush: 'sync' },
		)

		/**
		 * 設定聲音狀態
		 * @param status {boolean} 聲音狀態
		 * @return {void}
		 */
		const setVoiceStatus = (status: boolean): void => {
			isMuted.value = status
		}

		/**
		 * 設定原文狀態
		 * @param status {boolean} 顯示狀態
		 * @return {void}
		 */
		const setTranslationStatus = (status: boolean): void => {
			showOriginText.value = status
		}

		/**
		 * 設定語音撥放狀態
		 * @param status {boolean} 撥放狀態
		 * @return {void}
		 */
		const setIsVoicePlaying = (status: boolean): void => {
			isVoicePlaying.value = status
		}

		/**
		 * 點擊外部時關閉彈窗
		 * @return {void}
		 */
		const handleOutsideOnClick = (): void => {
			// 將顯示語速的overlay關閉
			showSpeechSpeedOverlay.value = false
			// 將顯示文字放大縮小的彈窗關閉
			showScalableRange.value = false
			// 將顯示段距的彈窗關閉
			showSpacingRange.value = false
			// 將顯示語言選擇的彈窗關閉
			showLangOverlay.value = false
			// 將匯出的overlay關閉
			showExportOverlay.value = false
		}

		/**
		 * 關閉 QR Code modal
		 * @return {void}
		 */
		const handleShareQRCodeModalOnClose = (): void => {
			// 將 QR Code modal 關閉
			showQRCodeModal.value = false
		}

		/**
		 * 點擊分享按鈕
		 * @return {void}
		 */
		const handleShareButtonOnClick = (): void => {
			// 關閉其他彈窗
			handleOutsideOnClick()
			// 將分享的Modal設定為顯示
			showQRCodeModal.value = true
		}

		/**
		 * Audio Player Ended
		 * @return {void}
		 */
		const handleAudioOnEnded = (): void => {
			// 將撥放完成後設定為false
			setIsVoicePlaying(false)
			// 將索引導到下一個
			currentVoiceIndex.value = currentVoiceIndex.value! + 1
			// 嘗試播放下一筆
			tryPlayCurrentVoice()
		}

		/**
		 * 點擊語言彈窗的語言按鈕時
		 * @param lang {string} 語言代碼
		 * @return {void}
		 */
		const handleSelectLangModalOnChange = (lang: string): void => {
			// 設定當前選擇的翻譯語言
			setTargetLang(lang)
			// 回報數據
			report()

			// 換語言前沒靜音就停掉舊音檔，從同一句用新語言續播並起輪詢；各語言的 audioUrls 不清，換回來或點舊段直接用留著的 URL
			// 換到不支援 TTS 的語言時 ttsAvailable 的 watch 會在同一輪停掉並靜音，所以這裡不用再判斷
			if (!isMuted.value) {
				// 記下正在播的那一句
				const resumeIndex = currentVoiceIndex.value
				// 取消還在等的 canplaythrough，避免舊語言 buffer 完成時照樣播出來
				cancelPendingCanPlayThrough()
				// 停掉播放與輪詢
				stopVoicePlayback()
				// 15 秒跳過從換語言這一刻重新起算，等的是新語言的音檔，不能沿用舊語言已經等掉的時間
				headOfLineSkipper.reset()
				// 回到正在播的那一句
				currentVoiceIndex.value = resumeIndex
				// 繼續播並起輪詢
				startVoicePlayback()
			}

			// 新語言的內容換了一整批，譯文視窗強制回到最底
			translationPane.value?.forceBottom()
			// 取得語言名稱，找不到就直接用語言代碼
			const title =
				getSupportedAzureSTTLanguages().find(
					(item) => item.locale === lang,
				)?.title ?? lang
			// 提示翻譯語言已經換了
			window.$toast?.({
				message: t('Toast.LanguageChanged', { lang: title }),
			})
		}

		/**
		 * 點擊語言選擇彈窗的關閉按鈕
		 * @return {void}
		 */
		const handleSelectLangModalOnClose = (): void => {
			// 將語言選擇彈窗關閉
			showLangOverlay.value = false
		}

		/**
		 * 點擊翻譯按鈕
		 * @return {void}
		 */
		const handleTranslateToBtnOnClick = (): void => {
			// 將顯示語速的overlay關閉
			showSpeechSpeedOverlay.value = false
			// 將顯示文字放大縮小的彈窗關閉
			showScalableRange.value = false
			// 將顯示段距的彈窗關閉
			showSpacingRange.value = false
			// 顯示語言選擇的彈窗設定為開啟
			showLangOverlay.value = !showLangOverlay.value
			// 將匯出的overlay關閉
			showExportOverlay.value = false
		}

		/**
		 * SSE 連線開啟時
		 * @return {void}
		 */
		const handleSseOnOpen = (): void => {
			// 連線成功，清除排程中的重連 timer（若有）
			if (sseReconnectTimer.value !== null) {
				clearTimeout(sseReconnectTimer.value)
				sseReconnectTimer.value = null
			}
			// 連線成功後重置退避，下次斷線從 1s 重新計算
			sseBackoffMs.value = 1000
			// 連線成功，重置連續錯誤計數
			sseConsecutiveErrors.value = 0
			// open 事件視為有效活動，重置看門狗計時
			lastSseEventAt.value = Date.now()
			// 並非第一次連線
			if (isSseReconnect.value) {
				// 回報當前語言，另外避免會因為沒有語言而導致沒有音檔問題
				report()
				// nchan 不補發斷線期間的訊息，重新拉頻道設定補上漏掉的 is_tts_active 等狀態
				// 已在線的觀眾本身就算在 size 內，所以不套用人數上限檢查，避免滿員時被誤踢
				checkSessionLimit(false).catch((error) => {
					console.warn('[SSE] resync channel state failed', error)
				})
			} else {
				// 如果是第一次連線
				// 設定為重新連線
				isSseReconnect.value = true
			}
			// 啟動 renew heartbeat，每 30s 更新 viewer 存活 timestamp
			startRenewHeartbeat()
		}

		/**
		 * SSE 接收到 notify 訊息時
		 * @param event {MessageEvent} SSE 訊息事件
		 * @return {void}
		 */
		const handleSseOnMessage = (event: MessageEvent): void => {
			// 收到業務訊息，重置看門狗計時
			lastSseEventAt.value = Date.now()
			// 記錄 nchan event id，重連時用來向 nchan 要求補發（Last-Event-ID 語義）
			if (event.lastEventId) {
				lastSseEventId.value = event.lastEventId
			}
			// 取得結果
			const result = event.data as string
			// 資料不為空時
			if (!stringIsEmpty(result)) {
				// 解析 JSON
				const jsonData = JSON.parse(result)
				// 取得通知類型
				const { notify_type } = jsonData

				// 如果會議結束時
				if (notify_type === NotifyEnum.SessionStatusClosed) {
					// 跳轉至結束頁面
					router.push({ name: 'meetingEnd' })
				} else if (notify_type === NotifyEnum.SummaryFinished) {
					// 如果是會議摘要完成
					// 取得摘要
					getSummary()
				} else if (
					notify_type === NotifyEnum.SessionStatusTranslating
				) {
					// 新句子開始翻譯，前一句排好的隱藏要取消，兩句重疊時才不會被提早關掉
					if (indicatorHideTimer !== null) {
						// 取消排程中的隱藏
						clearTimeout(indicatorHideTimer)
						// 清空參照，下次翻譯完成才會重新排程
						indicatorHideTimer = null
					}
					// 顯示翻譯中指示器，連續收到也只是維持顯示
					isTranslating.value = true
				} else if (
					notify_type === NotifyEnum.SessionStatusTranslateFinished
				) {
					// 重複收到翻譯完成時以最後一次為準重新排程
					if (indicatorHideTimer !== null) {
						// 取消前一次排程中的隱藏，延遲改從最後一次翻譯完成起算
						clearTimeout(indicatorHideTimer)
					}
					// 延遲隱藏，下一句若緊接著開始翻譯就能取消，避免指示器閃爍
					indicatorHideTimer = setTimeout(() => {
						// 計時器已經觸發，清掉參照
						indicatorHideTimer = null
						// 延遲到期仍沒有新句子開始翻譯，隱藏指示器
						hideIndicator()
					}, INDICATOR_HIDE_DELAY_MS)
				} else if (notify_type === NotifyEnum.SessionStatusOnAir) {
					// 如果是講者在線通知
					// 將是否為線上模式設定為true
					isLive.value = true
				} else if (notify_type === NotifyEnum.SessionStatusOffAir) {
					// 如果是講者離線通知
					// 將是否為線上模式設定為false
					isLive.value = false
				} else if (
					[
						NotifyEnum.TranslateMessage,
						NotifyEnum.SyncMessage,
						NotifyEnum.OriginalMessage,
						NotifyEnum.MessageUpdate,
						NotifyEnum.MessageUpdateSync,
					].includes(notify_type)
				) {
					// 如果是翻譯訊息或同步訊息
					// 取得 訊息ID / 語言 / 翻譯文本 / 原文文本 / 講者
					const {
						id,
						locale,
						translated_text,
						original_text,
						speaker,
					} = jsonData

					// 講者不為空時 且 講者不是 unknown
					if (
						!stringIsEmpty(speaker) &&
						speaker?.trim().toLowerCase() !== 'unknown'
					) {
						// 嘗試尋找講者
						const findSpeaker = speakers.value.findIndex(
							(s) => s === speaker,
						)
						// 當找不到時
						if (findSpeaker === -1) {
							//將講者加入陣列
							speakers.value.push(speaker)
						}
					}

					// 用 Map 做 O(1) 查找，避免 findIndex 隨訊息量增加變慢
					const messageIndex = messageIndexMap.get(id) ?? -1
					// 如果找到訊息
					if (messageIndex !== -1) {
						// 取得當前訊
						const currentMessage = messages.value[messageIndex]
						// 取得要更新的訊息
						const updatedMessage = {
							...currentMessage,
							// 確保翻譯訊息欄位已初始化
							translated_text:
								currentMessage.translated_text || {},
							sync_text: currentMessage.sync_text || {},
						}

						// 講者名稱不為空時 且 講者名稱有變更時
						if (
							!stringIsEmpty(speaker) &&
							currentMessage.speaker !== speaker
						) {
							// 更新講者名稱
							updatedMessage.speaker = speaker
						}

						switch (notify_type) {
							// 如果是同步訊息
							case NotifyEnum.SyncMessage:
								// 創建新的 sync_text 對象
								updatedMessage.sync_text = {
									...updatedMessage.sync_text,
									[locale]: translated_text,
								}
								break
							// 是翻譯訊息
							case NotifyEnum.TranslateMessage:
								// 創建新的 translated_text 對象
								updatedMessage.translated_text = {
									...updatedMessage.translated_text,
									[locale]: translated_text,
								}
								break
							// 是原文訊息
							case NotifyEnum.OriginalMessage:
								updatedMessage.original_text = original_text
								break
							// 是原文編輯後的重譯結果
							case NotifyEnum.MessageUpdate:
								// 有帶 request_id 的是 Speaker 的重譯觸發事件，發出順序等於編輯順序，是原文唯一的真相源； 它沒有 translated_text，不能拿去覆蓋譯文
								if (jsonData.request_id) {
									// 原本的原文如果與新的原文不同的話才處理
									if (
										updatedMessage.original_text !==
										original_text
									) {
										// 更新為編輯後的新原文
										updatedMessage.original_text =
											original_text
									}
									break
								}
								// 不帶 request_id 的是 Worker 的 per-locale 重譯結果；二次編輯時兩條重譯 pipeline 並行，它帶的原文可能是上一次編輯的舊版，一律不動原文只更新該語言的新譯文
								updatedMessage.translated_text = {
									...updatedMessage.translated_text,
									[locale]: translated_text,
								}
								break
							// 是原文編輯後的同步翻譯結果
							case NotifyEnum.MessageUpdateSync:
								// 更新該語言的口譯文字
								updatedMessage.sync_text = {
									...updatedMessage.sync_text,
									[locale]: translated_text,
								}
								break
						}

						// 更新訊息
						messages.value[messageIndex] = updatedMessage
					} else {
						// 如果沒有找到訊息
						// 新訊息
						const message = {
							...jsonData,
							sent_at: jsonData.created_at,
							message_id: id,
							type: MessageType.TranscriptMessage,
							translated_text: {},
							sync_text: {},
						}

						switch (notify_type) {
							// 如果是同步訊息
							case NotifyEnum.SyncMessage:
								// 創建新的 sync_text 對象
								message.sync_text[locale] = translated_text
								break
							// 是翻譯訊息
							case NotifyEnum.TranslateMessage:
								message.translated_text[locale] =
									translated_text
								break
							// 是原文訊息
							case NotifyEnum.OriginalMessage:
								message.original_text = original_text
								break
							// 是原文編輯後的重譯結果
							case NotifyEnum.MessageUpdate:
								// 寫入編輯後的新原文；訊息在本地不存在時沒有其他原文來源，結果事件的原文照寫當初始值
								message.original_text = original_text
								// 觸發事件（帶 request_id）沒有 translated_text，寫入會把該語言譯文設成 undefined
								if (!jsonData.request_id) {
									// 寫入該語言的新譯文
									message.translated_text[locale] =
										translated_text
								}
								break
							// 是原文編輯後的同步翻譯結果
							case NotifyEnum.MessageUpdateSync:
								// 寫入該語言的口譯文字
								message.sync_text[locale] = translated_text
								break
						}

						// 依 sent_at（client 講話時間，微秒）排序插入：翻譯慢句被超車時晚到的訊息要插回正確位置
						// 不能用 message_id 尾段序號當排序鍵——那是 STT session 內的流水號，speaker 暫停調設定後會歸零，
						// 新 session 的 -0 會被錯插到舊 session 的 -1 前面；講話時間才是跨 session 單調的
						const newSentAt =
							message.sent_at ?? Number.MAX_SAFE_INTEGER
						// 找到第一個 sent_at 大於新訊息的索引；相同 sent_at 維持先到先排，插入點在其後
						const insertIndex = messages.value.findIndex(
							(m) => (m.sent_at ?? 0) > newSentAt,
						)

						// 若沒有找到更大的序號則插入到最後
						if (insertIndex === -1) {
							// 記錄 index 到 Map，方便後續 O(1) 查找
							messageIndexMap.set(
								message.message_id,
								messages.value.length,
							)
							messages.value.push(message as never)
						} else {
							// 插入到對應位置以維持升序
							messages.value.splice(
								insertIndex,
								0,
								message as never,
							)
							// splice 會讓後續元素的 index 偏移，重建受影響的 Map entries
							for (
								let i = insertIndex;
								i < messages.value.length;
								i++
							) {
								messageIndexMap.set(
									messages.value[i].message_id,
									i,
								)
							}
						}

						// 如果是靜音的話
						if (isMuted.value) {
							// 將音檔索引設定為最後一筆訊息
							currentVoiceIndex.value = messages.value.length - 1
						}
					}
				} else if (notify_type === NotifyEnum.TitleChange) {
					// 如果是標題變更
					// 取得標題
					const { title } = jsonData
					// 設定標題
					setMeetTitle(title)
				} else if (notify_type === NotifyEnum.SpeakerUpdate) {
					//講者更新時
					// 取得 講者資訊 / 頻道或訊息ID / 顏色索引
					const { speaker, event, color_index } = jsonData
					// 講者存在的話
					if (speaker) {
						// 取得講者的屬性鍵
						const keys = Object.keys(speaker)
						// 講者存在 且 只有一個屬性時
						if (keys?.length === 1) {
							// 舊的講者名稱
							const oldName = keys[0]
							// 新的講者名稱
							const newName = speaker[oldName]
							// 尋找舊的講者名稱在陣列中的索引
							const findNumber = speakers.value.findIndex(
								(speaker) => speaker === oldName,
							)
							// 尋找訊息是否存在
							const findMessage = messages.value.findIndex(
								(message) => message.id === event,
							)

							// 找到舊的講者索引的話
							if (findNumber >= 0) {
								// 沒有找到訊息時代表要更新全部相同名稱的講者
								if (findMessage === -1) {
									// 更新講者名稱
									speakers.value[findNumber] = newName
									// 更新訊息中的講者名稱
									messages.value = messages.value.map(
										(message) => {
											// 如果訊息的講者名稱等於舊的講者名稱時
											if (message.speaker === oldName) {
												// 回傳新的結構
												return {
													...message,
													speaker: newName,
												}
											}

											// 否則回傳舊的結構
											return message
										},
									)
								} else {
									// 有找到的話代表僅是更新某一筆訊息內的講者名稱
									// 將講者名稱新增到講者的陣列中
									speakers.value.push(newName)
									// 將該筆訊息的講者名稱改為新的講者名稱
									messages.value[findMessage].speaker =
										newName
								}
							}
						}
					}

					// 如果講者名單尚未初始化 且 顏色索引存在的話
					if (speakers.value.length === 0 && color_index) {
						// 設定講者初始化名單
						speakers.value = (
							Object.entries(color_index) as [string, number][]
						)
							.filter(([oldName, _]) => oldName !== 'Unknown')
							.sort((a, b) => a[1] - b[1])
							.map(([oldName, _]) => oldName)
					}
				} else if (notify_type === NotifyEnum.TTS_Finish) {
					const { id, language, audio_url } = jsonData
					const msgIdx = messages.value.findIndex(
						(m) => m.message_id === id,
					)
					// 不過濾語言：Worker 在翻譯當下就對所有語言廣播且不重送，每個語言的音檔都要存進自己那一格，換語言後點舊段才有得播
					if (
						msgIdx !== -1 &&
						typeof language === 'string' &&
						language &&
						audio_url
					) {
						const existing = messages.value[msgIdx]
						// 同語言已經有音檔就是重送，保留第一次的 URL 不覆寫
						if (!existing.audioUrls?.[language]) {
							messages.value[msgIdx] = {
								...existing,
								audioUrls: {
									...existing.audioUrls,
									[language]: audio_url,
								},
								// 記下收到時間，存太久時才知道要重抓
								audioUrlReceivedAt: {
									...existing.audioUrlReceivedAt,
									[language]: Date.now(),
								},
							}
							// 只有目前語言的音檔、輪到這筆且沒在播才觸發播放，別語言的只存不播
							if (
								language === targetLang.value &&
								msgIdx === currentVoiceIndex.value &&
								!isMuted.value &&
								!isVoicePlaying.value
							) {
								tryPlayCurrentVoice()
							}
						}
					}
				} else if (notify_type === NotifyEnum.ChannelCapabilityChange) {
					// TTS開關改動時
					// 取得 TTS 啟用狀態
					const { tts } = jsonData
					// 設定 TTS 狀態
					isTTSActive.value = tts
				} else if (notify_type === NotifyEnum.ChannelReset) {
					// 頻道設定重置完成
					// 後端套用 reset 後發此收尾訊號；payload 不帶欄位值，需重新拉取完整 meta
					// 複用 init 流程的 checkSessionLimit，同步 title / license / capacity / is_tts_active 等設定
					checkSessionLimit()
				} else if (notify_type === NotifyEnum.ChannelPurge) {
					// 頻道歷史紀錄被清除
					// 清空本地訊息與 Map
					messages.value = []
					messageIndexMap.clear()
					// 重置音檔播放索引
					currentVoiceIndex.value = null
				} else if (notify_type === NotifyEnum.ChannelReplace) {
					// 頻道被替換為新頻道
					// 取得新頻道的 code 與 id
					const { new_channel_code, new_channel_id } = jsonData
					// 取消排程中的重連，避免跳頁後又觸發舊頻道的 connect()
					if (sseReconnectTimer.value !== null) {
						clearTimeout(sseReconnectTimer.value)
						sseReconnectTimer.value = null
					}
					// 停止看門狗，頻道切換後由新頻道的 connect() 重新啟動
					if (sseWatchdogTimer.value !== null) {
						clearInterval(sseWatchdogTimer.value)
						sseWatchdogTimer.value = null
					}
					// 關閉目前 SSE 連線，讓後端 subscription 自然釋放
					sse.value?.close()
					sse.value = null
					// 重置 Info Store 並以新 code 重新初始化
					setShortID(new_channel_code)
					setSessionID(new_channel_id)
					// 跳轉到首頁並帶入新 code，走既有首頁進入流程（重抓 meta / 語言選擇）
					router.push({
						name: 'home',
						query: { s: new_channel_code },
					})
				} else if (notify_type === NotifyEnum.ChannelSessionRenewed) {
					// 子頻道換場
					// speaker 開啟新一場後，後端廣播此通知
					// 整頁重載以重新建立新 SSE 連線、進入當前場統計
					window.location.reload()
				}
			}
		}

		/**
		 * 停掉播放與輪詢：暫停、停 interval、清鎖與播放旗標，索引移到最後一筆（有訊息時）
		 * @return {void}
		 */
		const stopVoicePlayback = (): void => {
			// 暫停撥放
			audioPlayer.value?.pause()
			// 停止輪詢 interval
			if (audioPlayInterval.value !== null) {
				clearInterval(audioPlayInterval.value)
				audioPlayInterval.value = null
			}
			// 將輪詢狀態重置，避免殘留鎖
			voicePollingInProgress.value = false
			// 換下一代，還在飛的舊請求回來時才會被丟掉
			voicePollingGeneration++
			// 有訊息時，將撥放音檔位置設定為最後一筆
			if (!arrayIsEmpty(messages.value)) {
				currentVoiceIndex.value = messages.value.length - 1
			}
			// 停播後再開（取消靜音、換語言）是新的一輪，第一次開始緩衝要重新起算 15 秒
			skipperResetIndex = null
			// 將播放狀態設定為false
			isVoicePlaying.value = false
		}

		/**
		 * 從目前索引開始播並起輪詢自癒
		 * @return {void}
		 */
		const startVoicePlayback = (): void => {
			// 如果索引index為空的話
			if (numberIsEmpty(currentVoiceIndex.value)) {
				// 將其設定為0 避免未被設定
				currentVoiceIndex.value = 0
			}
			// 先嘗試播放當前索引的音檔（事件快路徑）
			tryPlayCurrentVoice()
			// 啟動輪詢自癒 interval；每 500ms 觸發一次，runVoiceTick 內部含防重入與卡死自癒
			if (audioPlayInterval.value === null) {
				audioPlayInterval.value = setInterval(runVoiceTick, 500)
			}
		}

		/**
		 * 點擊聲音按鈕
		 * @return {void}
		 */
		const handleVoiceOnClick = (): void => {
			// 點擊時關閉其他彈窗
			handleOutsideOnClick()
			// 設定聲音狀態
			setVoiceStatus(!isMuted.value)
			// 如果是靜音的話
			if (isMuted.value) {
				// 取消還在等的 canplaythrough，避免緩衝完成時在靜音狀態下照樣播出來
				cancelPendingCanPlayThrough()
				// 停掉播放與輪詢
				stopVoicePlayback()
				return
			}

			// 沒有訊息時也照樣起輪詢，男聲頻道收不到 tts_finish、女聲頻道漏掉 tts_finish 時都要靠輪詢取音檔與 15 秒跳過才播得出來
			// 沒有訊息時索引會停在第 0 筆並越界，runVoiceTick 與 startVoicePolling 遇到越界直接返回，不會打 Mp3Url
			startVoicePlayback()
		}

		/**
		 * 跳到指定那一筆從那筆開始播；播放中會先停掉舊的，靜音中視為使用者手勢直接解除靜音
		 * @param index {number} 目標訊息在 messages 裡的絕對索引
		 * @return {void}
		 */
		const seekVoiceTo = (index: number): void => {
			// 索引越界不動
			if (index < 0 || index >= messages.value.length) return

			// 取消還在等的 canplaythrough，避免舊句 buffer 完成時照樣播出來
			cancelPendingCanPlayThrough()
			// 停掉正在播的舊句
			audioPlayer.value?.pause()
			// 播放旗標歸零，才不會被輪詢當成還在播
			setIsVoicePlaying(false)
			// 重置輪詢重入鎖，新那筆才能立刻開始等音檔
			voicePollingInProgress.value = false
			// 換下一代，舊那筆的冷卻結束時才不會解開新那筆的鎖
			voicePollingGeneration++
			// 指到目標那筆
			currentVoiceIndex.value = index
			// 使用者點段是主動操作，即使點的是同一句，開始緩衝時也要重新起算 15 秒
			skipperResetIndex = null

			// 靜音中：走既有的取消靜音流程（播當前筆並起輪詢）
			if (isMuted.value) {
				handleVoiceOnClick()
			} else {
				// 已取消靜音：直接播目標那筆
				tryPlayCurrentVoice()
			}

			// 目標那筆還沒有目前語言的音檔時立刻輪詢一次，不等下一個 500ms tick，點了才不會先空一拍
			if (!audioUrlOf(messages.value[index])) {
				startVoicePolling()
			}
		}

		/**
		 * 點 Go to Latest：跳到譯文最新一筆並從那筆播，中間跳過的句子不播
		 * @return {void}
		 */
		const handleCatchUpOnClick = (): void => {
			// 已追上就不動
			if (!isTtsBehind.value) return

			// 譯文視窗最新一句
			const items = translationPaneState.value.items
			// 跳過去
			seekVoiceTo(items[items.length - 1].index)
		}

		/**
		 * 點擊 TTS 項目：關 = 隱藏視窗並靜音，開 = 顯示視窗並取消靜音（這個點擊就是解除靜音的使用者手勢）
		 * @return {void}
		 */
		const handleTtsOnClick = (): void => {
			// 點擊時關閉其他彈窗
			handleOutsideOnClick()
			// 切換 TTS 視窗
			showTts.value = !showTts.value
			// 視窗狀態與靜音狀態不一致時翻轉靜音
			if (showTts.value === isMuted.value) {
				handleVoiceOnClick()
			}
			// 提示 TTS 視窗開了或關了
			window.$toast?.({
				message: t(showTts.value ? 'Toast.TtsOn' : 'Toast.TtsOff'),
			})
		}

		/**
		 * 點擊原文項目：顯示或隱藏原文視窗
		 * @return {void}
		 */
		const handleTranslationOnClick = (): void => {
			// 點擊時關閉其他彈窗
			handleOutsideOnClick()
			// 設定是否顯示原文的狀態
			setTranslationStatus(!showOriginText.value)
			// 提示原文視窗開了或關了
			window.$toast?.({
				message: t(
					showOriginText.value ? 'Toast.SourceOn' : 'Toast.SourceOff',
				),
			})
		}

		/**
		 * 點擊段距項目
		 * @return {void}
		 */
		const handleSpacingIconOnClick = (): void => {
			// 將顯示語速的overlay關閉
			showSpeechSpeedOverlay.value = false
			// 將顯示文字放大縮小的彈窗關閉
			showScalableRange.value = false
			// 將顯示語言選擇的彈窗關閉
			showLangOverlay.value = false
			// 切換段距彈窗的顯示狀態
			showSpacingRange.value = !showSpacingRange.value
			// 將匯出的overlay關閉
			showExportOverlay.value = false
		}

		/**
		 * 點擊縮放文字按鈕
		 * @return {void}
		 */
		const handleScalableTextIconOnClick = (): void => {
			// 將顯示語速的overlay關閉
			showSpeechSpeedOverlay.value = false
			// 將顯示段距的彈窗關閉
			showSpacingRange.value = false
			// 將顯示語言選擇的彈窗關閉
			showLangOverlay.value = false
			// 切換縮放文字的顯示狀態
			showScalableRange.value = !showScalableRange.value
			// 將匯出的overlay關閉
			showExportOverlay.value = false
		}

		/**
		 * 點擊語音速度按鈕
		 * @return {void}
		 */
		const handleSpeechSpeedIconOnClick = (): void => {
			// 將顯示文字放大縮小的彈窗關閉
			showScalableRange.value = false
			// 將顯示段距的彈窗關閉
			showSpacingRange.value = false
			// 將顯示語言選擇的彈窗關閉
			showLangOverlay.value = false
			// 切換語音速度的overlay顯示狀態
			showSpeechSpeedOverlay.value = !showSpeechSpeedOverlay.value
			// 將匯出的overlay關閉
			showExportOverlay.value = false
		}

		/**
		 * Banner 點擊事件，連點十次開啟除錯模式
		 * @return {void}
		 */
		const handleLogoOnClick = (): void => {
			// 計數器加1
			bannerClickCount.value++

			// 超過 1.5 秒沒點就重置計數（避免間隔太久）
			if (bannerClickTimer.value) {
				clearTimeout(bannerClickTimer.value)
			}

			// 設置計時器
			bannerClickTimer.value = setTimeout(
				() => (bannerClickCount.value = 0),
				1500,
			)

			// 如果連點十次
			if (bannerClickCount.value === 10) {
				// 將點擊次數歸零
				bannerClickCount.value = 0
				// 清除計時器
				clearTimeout(bannerClickTimer.value)
				// 切換除錯模式
				setIsDevMode(!isDevMode.value)
			}
		}

		/**
		 * 點擊匯出按鈕
		 * @return {void}
		 */
		const handleExportBtnOnClick = (): void => {
			// 將顯示語速的overlay關閉
			showSpeechSpeedOverlay.value = false
			// 將顯示文字放大縮小的彈窗關閉
			showScalableRange.value = false
			// 將顯示段距的彈窗關閉
			showSpacingRange.value = false
			// 將顯示語言選擇的彈窗關閉
			showLangOverlay.value = false
			// 根據點擊狀態決定匯出彈窗是否顯示
			showExportOverlay.value = !showExportOverlay.value
		}

		/**
		 * 當匯出彈窗關閉時
		 * @return {void}
		 */
		const handleExportOverlayOnClose = (): void => {
			// 將匯出彈窗關閉
			showExportOverlay.value = false
		}

		/**
		 * 頁面可見性變更處理：鎖屏喚醒後立即恢復 SSE 連線、Wake Lock 與 TTS 播放
		 *
		 * @returns {void}
		 */
		const handleVisibilityChange = (): void => {
			// 頁面隱藏中不處理
			if (document.hidden) return
			// 重置看門狗時間戳，避免隱藏期間靜默被誤判為殭屍
			lastSseEventAt.value = Date.now()
			// SSE 未處於連線中時立即重連，不等退避 timer
			if (!sse.value || sse.value.readyState !== EventSource.OPEN) {
				connect()
			}
			// Wake Lock 在頁面隱藏後自動釋放，重新申請
			requestWakeLock()

			// OS 鎖屏/切 APP 只會強制 pause <audio>、不發 ended/error，判斷 player 是否仍在正常播放中
			const playerActive =
				!!audioSource.value &&
				!!audioPlayer.value &&
				!audioPlayer.value.paused &&
				!audioPlayer.value.ended

			// 判斷佇列是否已播完（目前 index 已越過最後一筆訊息），播完不該跳回最後一筆重播
			const queueDrained =
				!numberIsEmpty(currentVoiceIndex.value) &&
				currentVoiceIndex.value! >= messages.value.length

			// 背景期間累積的連續卡死計數不可信（隱藏中已跳過看門狗，計數應該視為過期），呼叫規劃前先清空
			stuckPlaybackWatchdog.reset()
			// head-of-line 用牆鐘計時累積逾時，跨背景時間會被誤判為已逾時，同樣要在規劃前清空
			headOfLineSkipper.reset()
			// 回前景後不論停在哪一句，第一次開始緩衝都要重新起算 15 秒
			skipperResetIndex = null

			// 依目前靜音 / 訊息 / player / 佇列狀態規劃 TTS 恢復動作
			const plan = buildVisibilityResumePlan({
				isMuted: isMuted.value,
				hasMessages: !arrayIsEmpty(messages.value),
				latestIndex: messages.value.length - 1,
				playerActive,
				queueDrained,
			})

			// 重置輪詢重入鎖，避免隱藏期間卡在 true
			if (plan.resetPollingLock) {
				voicePollingInProgress.value = false
				// 換下一代，隱藏前那一輪的冷卻結束時才不會解開新一輪的鎖
				voicePollingGeneration++
			}
			// 卡死判定可能殘留在背景，清掉旗標避免誤判仍卡住而不重播
			if (plan.resetPlayingFlag) {
				setIsVoicePlaying(false)
			}
			// plan 判斷需要強制跳轉時才跳，對齊 mute→unmute 跳最新一筆的既有語義
			if (plan.jumpToIndex !== null) {
				// 跳最新前先取消 pending canplaythrough listener，否則被放棄的舊句 buffer 完成時仍會播出來，播完 ended handler 再 +1 把最新那筆整筆跳過
				cancelPendingCanPlayThrough()
				currentVoiceIndex.value = plan.jumpToIndex
			}
			// interval 隱藏期間可能已被清除，回前景需要補建才能繼續輪詢自癒
			if (plan.ensureInterval && audioPlayInterval.value === null) {
				audioPlayInterval.value = setInterval(runVoiceTick, 500)
			}
			// plan 判定需要搶播時才觸發，不必等下一輪輪詢才恢復播放
			if (plan.shouldTryPlay) {
				tryPlayCurrentVoice()
			}
		}

		/**
		 * 讀一次 audio 的播放進度給 TTS 視窗
		 * @return {void}
		 */
		const readTtsClock = (): void => {
			// 取得播放器
			const player = audioPlayer.value
			// 沒有播放器就不更新
			if (!player) return

			// 值跟上一次相同就不寫入（duration 可能是 NaN，用 Object.is 比），避免整頁每 80ms 重繪
			const prev = ttsClock.value
			if (
				prev &&
				Object.is(prev.currentTime, player.currentTime) &&
				Object.is(prev.duration, player.duration)
			) {
				return
			}

			// 記下目前播放秒數與總秒數
			ttsClock.value = {
				currentTime: player.currentTime,
				duration: player.duration,
			}
		}

		// 頻道關掉 TTS 或換到不支援 TTS 的語言時：停播、靜音並收掉視窗，避免視窗消失音檔還在播；再開時維持靜音，等使用者手勢
		watch(ttsAvailable, (available) => {
			// 還能用就不處理
			if (available) return

			// 靜音，之後新訊息進來索引才會跟到最新，再開 TTS 時從最新一句開始唸
			setVoiceStatus(true)
			// 取消還在等的 canplaythrough，避免舊音檔 buffer 完成時在靜音狀態下照樣播出來
			cancelPendingCanPlayThrough()
			// 停掉播放與輪詢，不再用不支援的語言打 Mp3Url
			stopVoicePlayback()
			// 收掉 TTS 視窗，再開時按鈕狀態才與靜音一致
			showTts.value = false
		})

		// 音檔開始播才起 80ms tick，停了就清掉
		watch(isVoicePlaying, (playing) => {
			// 先清掉舊的
			if (ttsTickTimer !== null) {
				clearInterval(ttsTickTimer)
				ttsTickTimer = null
			}
			// 沒在播：進度作廢
			if (!playing) {
				ttsClock.value = null
				return
			}

			// 立刻讀一次，之後每 80ms 讀一次
			readTtsClock()
			ttsTickTimer = setInterval(readTtsClock, TTS_TICK_MS)
		})

		/**
		 * 掛載時初始化
		 * @return {Promise<void>}
		 */
		onMounted(async () => {
			document.addEventListener(
				'visibilitychange',
				handleVisibilityChange,
			)
			await init()
		})

		/**
		 * 卸載時關閉 SSE 連線並停止音檔輪詢 interval
		 * @return {void}
		 */
		onUnmounted(() => {
			document.removeEventListener(
				'visibilitychange',
				handleVisibilityChange,
			)
			sse.value?.close()
			// 取消翻譯中指示器的隱藏計時，避免卸載後計時器還去改狀態
			hideIndicator()
			// 取消排程中的重連，避免元件卸載後還在背景重連
			if (sseReconnectTimer.value !== null) {
				clearTimeout(sseReconnectTimer.value)
				sseReconnectTimer.value = null
			}
			// 作廢飛行中的 Mp3Url 回應，卸載後才回來的結果不能再進 playMessage
			voicePollingGeneration++
			// 清除輪詢 interval，避免頁面離開後 interval 繼續執行造成記憶體洩漏
			if (audioPlayInterval.value !== null) {
				clearInterval(audioPlayInterval.value)
				audioPlayInterval.value = null
			}
			// 清除逐行揭露的 tick
			if (ttsTickTimer !== null) {
				clearInterval(ttsTickTimer)
				ttsTickTimer = null
			}
			// 清除看門狗 interval，避免元件卸載後繼續監控已關閉的連線
			if (sseWatchdogTimer.value !== null) {
				clearInterval(sseWatchdogTimer.value)
				sseWatchdogTimer.value = null
			}
			// 停止 renew heartbeat，避免元件卸載後繼續送 renew 請求
			stopRenewHeartbeat()
			// 清除殘留的 canplaythrough listener 閉包，避免元件卸載後仍持有 player 參照
			cancelPendingCanPlayThrough()
		})

		return {
			t,
			FONT_SIZES,
			GAP_STEPS,
			showVoiceButton,
			getTranscriptMessage,
			viewerID,
			isTranslating,
			sessionSummary,
			showSpeechSpeedOverlay,
			speechSpeed,
			showQRCodeModal,
			meetTitle,
			showScalableRange,
			showSpacingRange,
			audioSource,
			currentVoiceIndex,
			isVoicePlaying,
			audioPlayer,
			translationPane,
			showLangOverlay,
			targetLang,
			supportedTranslationLanguages,
			shortID,
			sessionID,
			targetGender,
			messages,
			fontSize,
			paragraphGap,
			isMuted,
			showTts,
			showOriginText,
			sse,
			isDevMode,
			bannerClickCount,
			bannerClickTimer,
			audioStyle,
			isLive,
			showExportOverlay,
			speakers,
			isTTSActive,
			ttsAvailable,
			showTtsPane,
			showPaneHead,
			showSpeakers,
			sourcePaneState,
			translationPaneState,
			ttsPaneState,
			isTtsBehind,
			ttsRevealClock,
			seekVoiceTo,
			handleCatchUpOnClick,
			handleOutsideOnClick,
			handleShareButtonOnClick,
			handleTranslateToBtnOnClick,
			handleScalableTextIconOnClick,
			handleSpacingIconOnClick,
			handleVoiceOnClick,
			handleSpeechSpeedIconOnClick,
			handleTtsOnClick,
			handleTranslationOnClick,
			handleSelectLangModalOnChange,
			handleSelectLangModalOnClose,
			handleShareQRCodeModalOnClose,
			handleLogoOnClick,
			handleExportBtnOnClick,
			handleExportOverlayOnClose,
		}
	},
})
</script>
<style lang="scss" scoped>
@use '@shared-assets/scss/variables' as *;

// 逐字稿畫面：頂欄以下撐滿，三個視窗與控制列垂直排列
.tutorial-shell {
	position: relative;
	height: 100%;
	display: flex;
	flex-direction: column;
}

// 三個視窗：TTS 橫跨上方，原文與譯文在下面各佔一半
.pane-stack {
	// 單一視窗的保證高度＝還看得到四行字：
	// 四行乘行高 1.2 乘字級，加標題列 40、捲動區上下 padding 32、上下外框 2（共 74）；
	// 畫面矮到四行塞不下時用 min() 夾上限：扣掉外框約 130、TTS 下限 140、兩條間距 20 之後的一半
	--pane-min: min(
		calc(4 * 1.2 * var(--transcript-font-size, 16px) + 74px),
		calc((100dvh - 290px) / 2)
	);
	flex: 1;
	min-height: 0;
	// 保險絲：任何情況都不要溢出到控制列上面
	overflow: hidden;
	display: flex;
	flex-direction: column;
	gap: var(--pane-gap);
	width: var(--content-width);
	max-width: var(--content-max-width);
	margin: 0 auto;
}

// 原文與譯文那一列：窄畫面上下疊，寬畫面左右並排
.pane-row {
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: var(--pane-gap);
	// 這一列要誠實回報自己的最小高度，否則 flex 會給它一個裝不下的高度，小孩直接溢出去而不是把壓力推回 TTS；
	// 不能用 min-content，捲動容器的 min-content 是整份逐字稿的高度
	min-height: var(--pane-min);

	// 有原文視窗時是兩個視窗疊起來
	&:has(.pane-source) {
		min-height: calc(var(--pane-min) * 2 + var(--pane-gap));
	}
}

// 原文與譯文各佔一半：同寬也同高
.pane-source,
.pane-translation {
	flex: 1 1 0;
	min-width: 0;
	min-height: 0;
}

// TTS 橫跨最上方，高度固定；可以縮到 min-height 為止，畫面矮的時候要跟著讓
.pane-tts {
	flex: 0 1 var(--tts-height);
	min-height: 140px;
}

// 寬畫面：原文與譯文左右並排、各佔一半，原文靠右只用 order，DOM 順序不動，窄畫面才會是 TTS、原文、譯文
@media (min-width: $breakpoint-md) {
	.pane-row {
		flex-direction: row;
	}

	// 並排時這一列只有一個視窗的高度
	.pane-row,
	.pane-row:has(.pane-source) {
		min-height: var(--pane-min);
	}

	.pane-translation {
		order: 0;
	}

	.pane-source {
		order: 1;
	}
}

// 標題列按鈕：Go to Latest 與語速共用同一套尺寸，高度寫死 20，文字做過 vertical trim
.pane-catch-up,
.pane-pill {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	height: 20px;
	padding: 0 8px;
	border: none;
	border-radius: var(--radius-title-button);
	background: var(--translation);
	color: #fff;
	font-family: inherit;
	font-size: var(--text-title-button-size);
	font-weight: var(--text-title-button-weight);
	white-space: nowrap;
	cursor: pointer;

	// hover 與 pressed 是實心按鈕，不能再疊底色，改把既有的填色壓暗；
	// 用 inset box-shadow 而不是換 background，一條規則同時適用藍底與 TTS 藍底上的白底按鈕
	&:hover:not(:disabled) {
		box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.08);
	}

	&:active:not(:disabled),
	&.is-open {
		box-shadow: inset 0 0 0 999px rgba(0, 0, 0, 0.16);
	}

	// 追上之後不隱藏改 disabled，按鈕一下有一下沒有標題列右側會跟著跳
	&:disabled {
		opacity: 0.45;
		cursor: default;
	}
}

// TTS 藍底上藍按鈕看不見，反過來用白底藍字
.pane-tts .pane-catch-up,
.pane-tts .pane-pill {
	background: #fff;
	color: var(--translation);
}

@supports (text-box-trim: trim-both) {
	.pane-catch-up > span,
	.pane-pill > span {
		text-box-trim: trim-both;
		text-box-edge: cap alphabetic;
	}
}
</style>
